import React, { useState } from "react";
import { ChevronDown, Search, Mail, AlertTriangle } from "lucide-react";
import { Link } from "react-router-dom";
import SubPageShell from "@/apps/rider/components/SubPageShell";

const TOPICS = [
  {
    group: "Booking & rides",
    items: [
      {
        q: "How is Link different from a rideshare?",
        a: "Link is a pre-scheduled, recurring round-trip service. You set the route once and we run it on the days you choose — no surge, no on-demand rides, no guessing if a driver will accept.",
      },
      {
        q: "Can I book a single trip without a subscription?",
        a: "Yes. When you confirm a route, pick One-time. You'll be charged once for that day's round-trip. Multi-day plans default to a weekly subscription but you can switch to One-time anytime.",
      },
      {
        q: "How do I edit my route or skip a day?",
        a: "Open Trips → tap the upcoming ride. From there you can change pickup time, switch days, or cancel an upcoming trip. Active subscriptions can be paused from your Account page.",
      },
      {
        q: "What service area do you cover?",
        a: "Routes up to 50 miles one-way are supported today. If you book outside that radius, the booking surface will surface an out-of-range card. Email support to request a special arrangement.",
      },
    ],
  },
  {
    group: "Driver & arrival",
    items: [
      {
        q: "How will I know my driver is on the way?",
        a: "Once a driver is assigned, your Trips screen shows a live status — Driver assigned → On the way → Arriving now → In ride. We also push a 5-minute pre-pickup alert so you can step outside on time.",
      },
      {
        q: "What if my driver is late?",
        a: "Tap your active ride from Trips for live status. If the assigned driver hasn't moved within 10 minutes of your pickup window, contact support and we'll re-route immediately.",
      },
    ],
  },
  {
    group: "Payment",
    items: [
      {
        q: "Where do I manage my saved cards?",
        a: <span>Open <Link to="/payment-methods" className="text-[#ed6500] underline-offset-4 hover:underline">Payment methods</Link> from the menu. You can set a default card, remove old ones, and add new cards via the secure Stripe portal.</span>,
      },
      {
        q: "When am I charged?",
        a: "Single-day trips: charged once when you confirm. Weekly plans: charged when you confirm, then again every Monday at 6 AM Central as long as the plan is active. We send a 48-hour heads-up before each renewal.",
      },
    ],
  },
];

export default function HelpCenter() {
  const [filter, setFilter] = useState("");
  const [open, setOpen] = useState({ 0: 0 }); // group index → expanded item index

  const lower = filter.trim().toLowerCase();
  const visibleGroups = TOPICS.map((g) => ({
    ...g,
    items: g.items.filter((it) => {
      if (!lower) return true;
      const aText = typeof it.a === "string" ? it.a : "";
      return it.q.toLowerCase().includes(lower) || aText.toLowerCase().includes(lower);
    }),
  })).filter((g) => g.items.length > 0);

  return (
    <SubPageShell
      eyebrow="Support"
      title="Help center"
      subtitle="Quick answers to the most common rider questions."
      back="/"
      backLabel="Home"
      testId="help-page"
    >
      <div className="relative mb-5">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#515E7B]" />
        <input
          type="text"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Search topics…"
          className="w-full pl-9 pr-3 py-2.5 rounded-xl bg-white border hairline text-[14px] text-[#0A1128] placeholder:text-[#515E7B] focus:outline-none focus:border-[#ed6500]/60"
          data-testid="help-search"
        />
      </div>

      {visibleGroups.length === 0 ? (
        <div className="bg-white border hairline rounded-2xl p-6 text-center text-sm text-[#515E7B]" data-testid="help-empty">
          No matches. Try a different keyword or contact support directly.
        </div>
      ) : (
        visibleGroups.map((group, gi) => (
          <section key={group.group} className="mb-5" data-testid={`help-group-${gi}`}>
            <div className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[#ed6500] mb-2">
              {group.group}
            </div>
            <div className="bg-white border hairline rounded-2xl divide-y divide-[#E5E1D8] overflow-hidden">
              {group.items.map((it, ii) => {
                const expanded = open[gi] === ii;
                return (
                  <div key={ii} data-testid={`help-q-${gi}-${ii}`}>
                    <button
                      type="button"
                      onClick={() => setOpen((o) => ({ ...o, [gi]: expanded ? -1 : ii }))}
                      className="w-full flex items-center justify-between gap-3 px-4 py-3.5 text-left"
                      aria-expanded={expanded}
                    >
                      <span className="font-semibold text-[#0A1128] text-[14.5px] leading-snug pr-3">{it.q}</span>
                      <ChevronDown
                        size={16}
                        strokeWidth={1.8}
                        className="text-[#0A1128] shrink-0 transition-transform duration-200"
                        style={{ transform: expanded ? "rotate(180deg)" : "rotate(0)" }}
                      />
                    </button>
                    <div
                      className="overflow-hidden transition-[max-height,opacity] duration-300 ease-out"
                      style={{ maxHeight: expanded ? 320 : 0, opacity: expanded ? 1 : 0 }}
                    >
                      <p className="text-[#515E7B] text-sm leading-relaxed px-4 pb-4 pr-6">
                        {it.a}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        ))
      )}

      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Link
          to="/support/contact"
          className="bg-[#0A1128] text-[#F9F7F4] rounded-2xl p-4 flex items-start gap-3 hover:bg-[#0A1128]/95 transition-colors"
          data-testid="help-cta-contact"
        >
          <span className="w-10 h-10 inline-flex items-center justify-center bg-white/10 text-[#ed6500] rounded-xl shrink-0">
            <Mail size={16} />
          </span>
          <div>
            <div className="font-semibold text-[15px]">Email support</div>
            <div className="text-[11.5px] text-[#F9F7F4]/70 mt-0.5">Real human, usually within a few hours.</div>
          </div>
        </Link>
        <Link
          to="/support/report"
          className="bg-white border hairline rounded-2xl p-4 flex items-start gap-3 hover:border-[#ed6500]/50 transition-colors"
          data-testid="help-cta-report"
        >
          <span className="w-10 h-10 inline-flex items-center justify-center bg-[#FCEADE] text-[#ed6500] rounded-xl shrink-0">
            <AlertTriangle size={16} />
          </span>
          <div>
            <div className="font-semibold text-[#0A1128] text-[15px]">Report an issue</div>
            <div className="text-[11.5px] text-[#515E7B] mt-0.5">Tell us what went wrong with a specific ride.</div>
          </div>
        </Link>
      </div>
    </SubPageShell>
  );
}
